import * as icons from '@mui/icons-material';
import {
  Autocomplete,
  type AutocompleteProps,
  Chip,
  TextField,
  type TextFieldProps,
} from '@mui/material';
import { useMemo } from 'react';
import type { FieldValues, UseControllerProps } from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';
import { SortableList } from '../SortableList.tsx';

export type SortableListFieldProps<T> = Omit<
  AutocompleteProps<T, true, false, false>,
  'renderInput'
> &
  Pick<Required<AutocompleteProps<T, true, false, false>>, 'getOptionKey' | 'getOptionLabel'> &
  UseControllerProps &
  Pick<TextFieldProps, 'label'>;

export const SortableListField = <T,>({
  name,
  rules,
  options,
  getOptionKey,
  getOptionLabel,
  label,
  ...props
}: SortableListFieldProps<T>) => {
  const { control } = useFormContext<FieldValues>();
  const { field, fieldState } = useController({
    name,
    control,
    rules,
  });

  const optionMap = useMemo(
    () => new Map([...field.value, ...options].map((item) => [getOptionKey(item), item])),
    [field.value, options, getOptionKey],
  );

  return (
    <Autocomplete<T, true, false, false>
      {...props}
      {...field}
      options={options}
      onChange={(_, value) => field.onChange(value)}
      getOptionKey={getOptionKey}
      getOptionLabel={getOptionLabel}
      multiple
      filterOptions={(options) => {
        const selected = new Set(field.value.map(getOptionKey));
        return options.filter((option) => !selected.has(getOptionKey(option)));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          error={!!fieldState.error}
          helperText={
            fieldState.error?.message ?? '順番をドラッグ＆ドロップで入れ替えることができます'
          }
        />
      )}
      renderTags={(tags, getTagProps) => {
        return (
          <SortableList
            sortStrategy="rect"
            ids={tags.map(getOptionKey)}
            onChange={(ids) => {
              field.onChange(ids.map((id) => optionMap.get(id)));
            }}
            renderItem={(_id, index) => {
              const { key: _, ...tagProps } = getTagProps({ index });
              const item = tags[index]!;

              return (
                <Chip
                  {...tagProps}
                  label={getOptionLabel(item)}
                  deleteIcon={
                    <icons.Cancel
                      onClick={(event) => {
                        tagProps.onDelete(event);
                      }}
                    />
                  }
                />
              );
            }}
          />
        );
      }}
    />
  );
};
